'use client'

import { useState, useSyncExternalStore } from 'react'

import { Button } from '@/components/ui/button'
import { Check, Loader2 } from '@/components/ui/icons'
import { Textarea } from '@/components/ui/textarea'
import { submitFeedback } from '@/lib/api'
import { captureSentryError } from '@/lib/sentry'

interface GenerationApiAccessRequestPanelProps {
  organizationId: string
  organizationName?: string
}

interface GenerationApiAccessErrorPanelProps {
  onRetry: () => void
}

const ACCESS_REQUEST_STORAGE_PREFIX = 'martini:generation-api-access-requested:'
const ACCESS_REQUEST_CHANGED_EVENT = 'martini:generation-api-access-request-changed'
const MIN_USE_CASE_LENGTH = 40
const MAX_USE_CASE_LENGTH = 1000

export function generationApiAccessRequestStorageKey(organizationId: string): string {
  return `${ACCESS_REQUEST_STORAGE_PREFIX}${organizationId}`
}

function hasPersistedAccessRequest(organizationId: string): boolean {
  try {
    return window.localStorage.getItem(generationApiAccessRequestStorageKey(organizationId)) === '1'
  } catch {
    return false
  }
}

function persistAccessRequest(organizationId: string): void {
  try {
    window.localStorage.setItem(generationApiAccessRequestStorageKey(organizationId), '1')
    window.dispatchEvent(new Event(ACCESS_REQUEST_CHANGED_EVENT))
  } catch {
    // The successful in-memory state still prevents duplicate clicks for this visit.
  }
}

function subscribeToPersistedAccessRequests(onStoreChange: () => void): () => void {
  window.addEventListener('storage', onStoreChange)
  window.addEventListener(ACCESS_REQUEST_CHANGED_EVENT, onStoreChange)
  return () => {
    window.removeEventListener('storage', onStoreChange)
    window.removeEventListener(ACCESS_REQUEST_CHANGED_EVENT, onStoreChange)
  }
}

export function buildGenerationApiAccessRequest(organizationId: string, useCase: string, organizationName?: string) {
  const organization = organizationName ? `${organizationName} (${organizationId})` : `organization ${organizationId}`
  return {
    message: `Request Generation API preview access for ${organization}.\n\nUse case:\n${useCase.trim()}`,
    category: 'Generation API Access',
    priority: 'Medium',
  }
}

export function GenerationApiAccessRequestPanel({
  organizationId,
  organizationName,
}: GenerationApiAccessRequestPanelProps) {
  const [useCase, setUseCase] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [requestedThisVisit, setRequestedThisVisit] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const persistedRequest = useSyncExternalStore(
    subscribeToPersistedAccessRequests,
    () => hasPersistedAccessRequest(organizationId),
    () => false,
  )
  const requested = requestedThisVisit === organizationId || persistedRequest
  const normalizedUseCase = useCase.trim()
  const hasEnoughDetail = normalizedUseCase.length >= MIN_USE_CASE_LENGTH

  const requestAccess = async () => {
    if (submitting || requested || !hasEnoughDetail) return

    setSubmitting(true)
    setError(null)
    try {
      await submitFeedback({
        ...buildGenerationApiAccessRequest(organizationId, normalizedUseCase, organizationName),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      })
      persistAccessRequest(organizationId)
      setRequestedThisVisit(organizationId)
    } catch (requestError) {
      setError('Failed to request access. Try again.')
      captureSentryError(requestError instanceof Error ? requestError : new Error(String(requestError)), {
        operation: 'request_generation_api_access',
        component: 'GenerationApiAccessRequestPanel',
        extra: { organizationId },
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card px-5 py-4">
      <form
        onSubmit={event => {
          event.preventDefault()
          void requestAccess()
        }}
        className="space-y-4"
      >
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-medium">API access</h2>
            <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Private preview
            </span>
          </div>
          <p className="mt-1.5 max-w-xl text-xs text-muted-foreground">
            We’re inviting a small group to shape the Martini API before wider release.
          </p>
        </div>

        {requested ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              Request sent. We’ll follow up by email.
            </p>
            <Button type="button" disabled className="shrink-0">
              <Check aria-hidden="true" />
              Access Requested
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <label
                htmlFor={`generation-api-use-case-${organizationId}`}
                className="text-xs font-medium text-foreground"
              >
                What would you build with it?
              </label>
              <Textarea
                id={`generation-api-use-case-${organizationId}`}
                value={useCase}
                onChange={event => setUseCase(event.target.value)}
                placeholder="Describe your workflow, the models you need, and where generated media should go."
                minLength={MIN_USE_CASE_LENGTH}
                maxLength={MAX_USE_CASE_LENGTH}
                rows={4}
                required
                disabled={submitting}
                aria-describedby={`generation-api-use-case-help-${organizationId}`}
                className="min-h-24 resize-y text-sm"
              />
              <div
                id={`generation-api-use-case-help-${organizationId}`}
                className="flex items-start justify-between gap-3 text-[11px] text-muted-foreground/60"
              >
                <p>Specific use cases help us prioritize preview access. Minimum {MIN_USE_CASE_LENGTH} characters.</p>
                <span className="shrink-0 tabular-nums">
                  {useCase.length} / {MAX_USE_CASE_LENGTH}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div aria-live="polite">{error ? <p className="text-xs text-destructive">{error}</p> : null}</div>
              <Button type="submit" disabled={submitting || !hasEnoughDetail} className="shrink-0">
                {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                {submitting ? 'Requesting...' : 'Request Preview Access'}
              </Button>
            </div>
          </>
        )}
      </form>
    </section>
  )
}

export function GenerationApiAccessErrorPanel({ onRetry }: GenerationApiAccessErrorPanelProps) {
  return (
    <section className="rounded-lg border border-border bg-card px-5 py-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-medium">API access</h2>
          <p className="mt-1 max-w-xl text-xs text-muted-foreground">
            We couldn’t check Martini API access. Retry before managing keys or requesting access.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onRetry} className="shrink-0">
          Retry
        </Button>
      </div>
    </section>
  )
}
